import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Container } from '@mui/system';
import { Button, CircularProgress, Fade, Paper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FlightSearchHero from '~/components/FlightSearchHero';
import { menuItem } from '~/components/HeroMenuButtons';
import IcontTextXColumns from '~/components/IcontTextXColumns';
import TripPayoutsProviderBuyTracker from '~/components/TripPayoutsProviderBuyTracker';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PaymentIcon from '@mui/icons-material/Payment';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTravelpayoutsLocations } from '~/actions';
import { FlightSearchProvider } from '~/types/flights';
import { RootState, TravelpayoutsLocation } from '~/types';
import { Currency } from '~/types/common';
import { CONFIG } from '~/config';
import { getLowestDisplayedTicketPrice } from '~/utils/ticketPrices';

type ParsedFlightSearch = {
  originCode: string;
  destinationCode?: string;
  departureDate?: string;
  returnDate?: string;
  passengerCount: number;
  travelClass: string;
};

const MONTH_COUNT = 12;
const MAX_PASSENGER_COUNT = 9;
const DAY_MONTH_LENGTH = 4;

const TRAVELPAYOUTS_CLASS_LABELS: Record<string, string> = {
  c: 'Business',
};

type CheapFlightsRouteParams = {
  originIata?: string;
  destinationIata?: string;
};

type PriceIntelligencePoint = {
  price?: number;
  currency?: string;
  departureDate?: string;
  returnDate?: string;
};

type PriceIntelligenceRoute = {
  origin?: string;
  destination?: string;
  bestReturnFlights?: PriceIntelligencePoint[];
  bestDateGridPrices?: PriceIntelligencePoint[];
  allDateGridPrices?: PriceIntelligencePoint[];
};

type FlexibleDatesOffer = {
  price: number;
  currency: Currency;
  originCityCode: string;
  destinationCityCode: string;
  originCityName: string;
  destinationCityName: string;
  formattedPrice: string;
  url: string;
};

const LEGACY_CHEAP_FLIGHTS_QUERY_PARAMS = [
  'outbound',
  'outbound1',
  'outbound2',
  'inbound',
  'via_airport1',
  'via_airport2',
  'adults',
  'children',
  'infant',
  'infants',
  'class',
];

const IATA_CODE_PATTERN = /^[A-Z]{3}$/;
const PRICE_INTELLIGENCE_REFERRAL_PARAM = 'price_intelligence';
const FLIGHT_MATRIX_SELECTOR = [
  '[class*="FlightMatrixDesktop-module__root___"]',
  '[class*="FlightMatrixMobile-module__flightMatrixMobile___"]',
].join(',');
const FLIGHT_MATRIX_CLASS_PREFIXES = [
  'FlightMatrixDesktop-module__root___',
  'FlightMatrixMobile-module__flightMatrixMobile___',
];

const formatTravelDate = (dayMonth?: string) => {
  if (!dayMonth || dayMonth.length !== DAY_MONTH_LENGTH) return '';

  const day = Number(dayMonth.slice(0, 2));
  const month = Number(dayMonth.slice(2, 4));

  if (!day || !month || month < 1 || month > MONTH_COUNT) return '';

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(new Date(2024, month - 1, day));
};

const parseFlightSearch = (flightSearch: string | null): ParsedFlightSearch | null => {
  if (!flightSearch) return null;

  const normalizedFlightSearch = flightSearch.toUpperCase();
  const multiCityMatch = normalizedFlightSearch.match(/^([A-Z]{3})((?:\d{4}[A-Z]{3}){2,3})(C)?(\d*)$/);

  if (multiCityMatch) {
    const [, originCode, routeSegments, classCode = '', passengerDigits = ''] = multiCityMatch;
    const destinationCode = routeSegments.slice(-3);
    const departureDate = routeSegments.slice(0, 4);
    const passengerCount = passengerDigits
      .split('')
      .reduce((total, digit) => total + Number(digit), 0) || 1;

    return {
      originCode,
      destinationCode,
      departureDate,
      passengerCount: Math.max(1, Math.min(MAX_PASSENGER_COUNT, passengerCount)),
      travelClass: TRAVELPAYOUTS_CLASS_LABELS[classCode.toLowerCase()] || 'Economy',
    };
  }

  const match = normalizedFlightSearch.match(/^([A-Z]{3})(?:(\d{4})([A-Z]{3})(\d{4})?|([A-Z]{3}))([A-Z])?(\d*)$/);

  if (!match) return null;

  const [, originCode, departureDate, datedDestinationCode, returnDate, undatedDestinationCode, classCode = '', passengerDigits = ''] = match;
  const destinationCode = datedDestinationCode || undatedDestinationCode;
  const passengerCount = passengerDigits
    .split('')
    .reduce((total, digit) => total + Number(digit), 0) || 1;

  return {
    originCode,
    destinationCode,
    departureDate,
    returnDate,
    passengerCount: Math.max(1, Math.min(MAX_PASSENGER_COUNT, passengerCount)),
    travelClass: TRAVELPAYOUTS_CLASS_LABELS[classCode.toLowerCase()] || 'Economy',
  };
};

const getLocationLabel = (location: TravelpayoutsLocation | null | undefined, fallbackCode: string) => {
  if (!location) return fallbackCode;

  return location.type === 'airport'
    ? `${location.cityName || location.name} (${location.code})`
    : `${location.name} (${location.code})`;
};

const getLocationCityName = (location: TravelpayoutsLocation | null | undefined, fallbackCode: string) => {
  if (!location) return fallbackCode;

  return location.type === 'airport'
    ? location.cityName || location.name
    : location.name;
};

const getLocationCityCode = (location: TravelpayoutsLocation | null | undefined, fallbackCode: string) => {
  const code = location?.type === 'airport' ? location.cityCode : location?.code;

  return (code || fallbackCode).trim().toUpperCase();
};

const toRouteSlug = (cityName: string) => cityName
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const getUpcomingCheapestPoint = (route: PriceIntelligenceRoute) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const seen = new Set<string>();

  return [
    ...(route.bestReturnFlights || []),
    ...(route.bestDateGridPrices || []),
    ...(route.allDateGridPrices || []),
  ]
    .filter((point) => {
      const price = Number(point.price);
      const departureDate = point.departureDate ? new Date(`${point.departureDate}T00:00:00`) : null;
      const key = `${point.departureDate}|${point.returnDate}|${point.currency}|${price}`;

      if (!Number.isFinite(price) || price <= 0 || !departureDate || Number.isNaN(departureDate.getTime()) || departureDate < today || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .sort((a, b) => Number(a.price) - Number(b.price))[0];
};

const getDeepSearchRoots = (container: HTMLElement) => {
  const roots: Array<HTMLElement | ShadowRoot> = [container];

  if (container.shadowRoot) {
    roots.push(container.shadowRoot);
  }

  for (let index = 0; index < roots.length; index += 1) {
    roots[index].querySelectorAll<HTMLElement>('*').forEach((element) => {
      if (element.shadowRoot && !roots.includes(element.shadowRoot)) {
        roots.push(element.shadowRoot);
      }
    });
  }

  return roots;
};

const getFlightMatrixElements = (container: HTMLElement) => {
  const matrices = new Set<HTMLElement>();

  getDeepSearchRoots(container).forEach((root) => {
    root.querySelectorAll<HTMLElement>(FLIGHT_MATRIX_SELECTOR).forEach(matrix => matrices.add(matrix));

    root.querySelectorAll<HTMLElement>('*').forEach((element) => {
      const className = typeof element.className === 'string'
        ? element.className
        : element.getAttribute('class') || '';

      if (FLIGHT_MATRIX_CLASS_PREFIXES.some(prefix => className.includes(prefix))) {
        matrices.add(element);
      }
    });
  });

  return Array.from(matrices);
};

const containsFlightFilters = (container: HTMLElement) => {
  const isFlightFilters = (element: Element) => Array.from(element.classList)
    .some(className => className.startsWith('FlightFilters'));

  // Never remove a shared matrix/filter container, including nested shadow hosts.
  return isFlightFilters(container) || getDeepSearchRoots(container).some(root => (
    Array.from(root.querySelectorAll('[class]')).some(isFlightFilters)
  ));
};

const formatRouteDateToDayMonth = (date?: string | null) => {
  if (!date) return '';

  const compactDate = date.replace(/[^0-9]/g, '');

  if (compactDate.length === DAY_MONTH_LENGTH) return compactDate;

  if (compactDate.length === 8) {
    return `${compactDate.slice(6, 8)}${compactDate.slice(4, 6)}`;
  }

  return '';
};

const getRouteIataCode = (params: URLSearchParams, paramName: string) => {
  const code = params.get(paramName)?.trim().toUpperCase();

  return code && IATA_CODE_PATTERN.test(code) ? code : '';
};

const buildFlightSearchFromCheapFlightsRoute = (routeParams: CheapFlightsRouteParams) => {
  if (typeof window === 'undefined') return null;

  const isOriginOnlyRoute = routeParams.originIata?.toLowerCase() === 'from';
  const originCode = (isOriginOnlyRoute ? routeParams.destinationIata : routeParams.originIata)?.toUpperCase();
  const destinationCode = isOriginOnlyRoute ? undefined : routeParams.destinationIata?.toUpperCase();

  if (!originCode || (!destinationCode && !isOriginOnlyRoute)) return null;

  const params = new URLSearchParams(window.location.search);
  const hasLegacySearchDates = params.has('outbound') || params.has('outbound1') || params.has('outbound2') || params.has('inbound');
  const hasViaAirports = params.has('via_airport1') || params.has('via_airport2');

  if (params.has('flightSearch') && !hasLegacySearchDates && !hasViaAirports) return null;

  const outbound = formatRouteDateToDayMonth(params.get('outbound'));
  const outbound1 = formatRouteDateToDayMonth(params.get('outbound1'));
  const outbound2 = formatRouteDateToDayMonth(params.get('outbound2'));
  const inbound = formatRouteDateToDayMonth(params.get('inbound'));
  const viaAirport1 = getRouteIataCode(params, 'via_airport1');
  const viaAirport2 = viaAirport1 ? getRouteIataCode(params, 'via_airport2') : '';
  const adults = Math.max(1, Number(params.get('adults') || 1));
  const children = Math.max(0, Number(params.get('children') || 0));
  const infants = Math.max(0, Number(params.get('infant') || params.get('infants') || 0));
  const travelClass = params.get('class')?.toLowerCase() === 'c' ? 'c' : '';

  const passengerSuffix = `${adults}${children ? children : ''}${infants ? infants : ''}`;

  if (isOriginOnlyRoute) return `${originCode}${travelClass}${passengerSuffix}`;

  if (!destinationCode) return null;

  if (!outbound) return `${originCode}${destinationCode}${travelClass}${passengerSuffix}`;

  if (viaAirport1 && outbound1) {
    const routeSegments = [`${originCode}${outbound}${viaAirport1}`, `${outbound1}`];

    if (viaAirport2 && outbound2) {
      routeSegments.push(viaAirport2, outbound2);
    }

    routeSegments.push(destinationCode);

    return `${routeSegments.join('')}${travelClass}${passengerSuffix}`;
  }

  return `${originCode}${outbound}${destinationCode}${inbound}${travelClass}${passengerSuffix}`;
};

const syncFlightSearchUrl = (routeFlightSearch: string | null, params: URLSearchParams) => {
  if (!routeFlightSearch) return;

  params.set('flightSearch', routeFlightSearch);
  LEGACY_CHEAP_FLIGHTS_QUERY_PARAMS.forEach(paramName => params.delete(paramName));
  window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
};

const getInitialFlightSearch = (routeParams: CheapFlightsRouteParams) => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const routeFlightSearch = buildFlightSearchFromCheapFlightsRoute(routeParams);
  const flightSearch = routeFlightSearch || params.get('flightSearch');

  if (routeFlightSearch) {
    syncFlightSearchUrl(routeFlightSearch, params);
  }

  return parseFlightSearch(flightSearch);
};

const isReadyToSearchFlights = (parsedSearch: ParsedFlightSearch | null) => Boolean(parsedSearch?.departureDate);

function FlightHomepage() {
  const routeParams = useParams<CheapFlightsRouteParams>();
  const dispatch = useDispatch();
  const locationsByCode = useSelector<RootState, Record<string, TravelpayoutsLocation | null>>(
    ({ locations }) => locations.travelpayoutsLocations,
  );
  const currency = useSelector<RootState, Currency>(({ app }) => app.currency);
  const initialFlightSearch = getInitialFlightSearch(routeParams);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [parsedFlightSearch, setParsedFlightSearch] = useState<ParsedFlightSearch | null>(initialFlightSearch);
  const [hideIntroText, setHideIntroText] = useState(() => isReadyToSearchFlights(initialFlightSearch));
  const [showPayoutsLoading, setShowPayoutsLoading] = useState(false);
  const [payoutsSearchSubmitted, setPayoutsSearchSubmitted] = useState(() => isReadyToSearchFlights(initialFlightSearch));
  const [showMobilePayoutsWidget, setShowMobilePayoutsWidget] = useState(false);
  const [flexibleDatesOffer, setFlexibleDatesOffer] = useState<FlexibleDatesOffer | null>(null);
  const [isCheapestDatesLoading, setIsCheapestDatesLoading] = useState(false);
  const [ticketResultsReady, setTicketResultsReady] = useState(false);
  const [lowestDisplayedTicketPrice, setLowestDisplayedTicketPrice] = useState<number | null>(null);
  const ticketsWrapperRef = useRef<HTMLDivElement | null>(null);
  const ticketsRef = useRef<HTMLDivElement | null>(null);
  const [ticketsMinHeight, setTicketsMinHeight] = useState<number>();
  const flightProvider: FlightSearchProvider = FlightSearchProvider.TRAVELPAYOUTS; // Change this to switch providers

  useEffect(() => {
    const resetCheapestDatesLoading = () => setIsCheapestDatesLoading(false);

    window.addEventListener('pageshow', resetCheapestDatesLoading);

    return () => window.removeEventListener('pageshow', resetCheapestDatesLoading);
  }, []);

  const shouldUseMobilePayoutsMode = flightProvider === FlightSearchProvider.TRAVELPAYOUTS && isMobile && payoutsSearchSubmitted;

  useEffect(() => {
    const syncFlightSearchFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const routeFlightSearch = buildFlightSearchFromCheapFlightsRoute(routeParams);

      if (routeFlightSearch) {
        syncFlightSearchUrl(routeFlightSearch, params);
      }

      const parsedSearch = parseFlightSearch(routeFlightSearch || params.get('flightSearch'));
      setParsedFlightSearch(parsedSearch);

      if (isReadyToSearchFlights(parsedSearch)) {
        setHideIntroText(true);
        setPayoutsSearchSubmitted(true);
      }
    };

    syncFlightSearchFromUrl();
    window.addEventListener('popstate', syncFlightSearchFromUrl);

    return () => window.removeEventListener('popstate', syncFlightSearchFromUrl);
  }, [routeParams]);

  useEffect(() => {
    if (!parsedFlightSearch) return;

    const codes = [parsedFlightSearch.originCode, parsedFlightSearch.destinationCode].filter((code): code is string => Boolean(code));
    const missingCodes = codes.filter(code => !(code in locationsByCode));

    if (!missingCodes.length) return;

    dispatch(getTravelpayoutsLocations(missingCodes));
  }, [dispatch, locationsByCode, parsedFlightSearch]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams(window.location.search);
    const originLocation = parsedFlightSearch ? locationsByCode[parsedFlightSearch.originCode] : null;
    const destinationLocation = parsedFlightSearch?.destinationCode
      ? locationsByCode[parsedFlightSearch.destinationCode]
      : null;

    setFlexibleDatesOffer(null);

    if (
      !parsedFlightSearch?.destinationCode
      || !parsedFlightSearch.returnDate
      || params.get(PRICE_INTELLIGENCE_REFERRAL_PARAM) === '1'
      || originLocation === undefined
      || destinationLocation === undefined
      || !originLocation
      || !destinationLocation
    ) {
      return () => controller.abort();
    }

    const originCityCode = getLocationCityCode(originLocation, parsedFlightSearch.originCode);
    const destinationCityCode = getLocationCityCode(destinationLocation, parsedFlightSearch.destinationCode);
    const originCityName = getLocationCityName(originLocation, originCityCode);
    const destinationCityName = getLocationCityName(destinationLocation, destinationCityCode);

    if (
      !IATA_CODE_PATTERN.test(originCityCode)
      || !IATA_CODE_PATTERN.test(destinationCityCode)
      || originCityCode === destinationCityCode
    ) {
      return () => controller.abort();
    }

    const loadFlexibleDatesOffer = async () => {
      try {
        const supportUrl = new URL('v2/reference-data/supported-price-intelligence-route', CONFIG.env.apiBaseUrl);
        supportUrl.searchParams.set('search', originCityCode);
        supportUrl.searchParams.set('limit', '50');
        const supportResponse = await fetch(supportUrl, {
          signal: controller.signal,
          headers: {
            'X-Tripenger-Caller': 'tripenger-fe',
            'X-Tripenger-Page': window.location.pathname,
          },
        });

        if (!supportResponse.ok) return;

        const supportPayload = await supportResponse.json() as {
          data?: Array<{ type?: string; origin?: string; destination?: string }>;
        };
        const routeSupported = supportPayload.data?.some((suggestion) => (
          suggestion.type === 'route'
          && suggestion.origin === originCityCode
          && suggestion.destination === destinationCityCode
        ));

        if (!routeSupported) return;

        const pricesUrl = new URL('v2/reference-data/route-price-intelligence', CONFIG.env.apiBaseUrl);
        pricesUrl.searchParams.set('origin', originCityCode);
        pricesUrl.searchParams.set('destination', destinationCityCode);
        pricesUrl.searchParams.set('currency', currency);
        pricesUrl.searchParams.set('limit', '1');
        const pricesResponse = await fetch(pricesUrl, {
          signal: controller.signal,
          headers: {
            'X-Tripenger-Caller': 'tripenger-fe',
            'X-Tripenger-Page': window.location.pathname,
          },
        });

        if (!pricesResponse.ok) return;

        const pricesPayload = await pricesResponse.json() as { data?: PriceIntelligenceRoute[] };
        const route = pricesPayload.data?.find((item) => (
          item.origin === originCityCode && item.destination === destinationCityCode
        ));
        const cheapestPoint = route ? getUpcomingCheapestPoint(route) : undefined;

        if (!cheapestPoint?.price) return;
        if (cheapestPoint.currency && cheapestPoint.currency.toUpperCase() !== currency) return;

        const routePath = `/cheap-flights/${toRouteSlug(originCityName)}-to-${toRouteSlug(destinationCityName)}-flights-${originCityCode.toLowerCase()}-${destinationCityCode.toLowerCase()}/`;
        const infoSiteBaseUrl = CONFIG.app.helpCenterLink.replace(/\/$/, '');

        setFlexibleDatesOffer({
          price: Number(cheapestPoint.price),
          currency,
          originCityCode,
          destinationCityCode,
          originCityName,
          destinationCityName,
          formattedPrice: new Intl.NumberFormat('en-IE', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
          }).format(Number(cheapestPoint.price)),
          url: `${infoSiteBaseUrl}${routePath}`,
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to load flexible-date price intelligence.', error);
        }
      }
    };

    void loadFlexibleDatesOffer();

    return () => controller.abort();
  }, [currency, locationsByCode, parsedFlightSearch]);

  const updateTicketsMinHeight = useCallback(() => {
    const ticketsWrapper = ticketsWrapperRef.current;
    const ticketsContainer = ticketsRef.current;

    if (!ticketsWrapper || !ticketsContainer) return;

    const wrapperTop = ticketsWrapper.getBoundingClientRect().top;
    let bottom = ticketsContainer.getBoundingClientRect().bottom;

    const measureChildren = (root: ParentNode) => {
      root.childNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          const rect = node.getBoundingClientRect();
          bottom = Math.max(bottom, rect.bottom);

          if (node.shadowRoot) {
            measureChildren(node.shadowRoot);
          }

          measureChildren(node);
        }
      });
    };

    measureChildren(ticketsContainer);

    const nextMinHeight = Math.max(0, Math.ceil(bottom - wrapperTop));
    setTicketsMinHeight((currentMinHeight) => (
      currentMinHeight === nextMinHeight ? currentMinHeight : nextMinHeight
    ));
  }, []);

  useEffect(() => {
    if (flightProvider !== FlightSearchProvider.TRAVELPAYOUTS) return undefined;

    updateTicketsMinHeight();

    const ticketsContainer = ticketsRef.current;
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateTicketsMinHeight)
      : undefined;
    const mutationObserver = typeof MutationObserver !== 'undefined' && ticketsContainer
      ? new MutationObserver(updateTicketsMinHeight)
      : undefined;

    if (ticketsWrapperRef.current && resizeObserver) {
      resizeObserver.observe(ticketsWrapperRef.current);
    }

    if (ticketsContainer && resizeObserver) {
      resizeObserver.observe(ticketsContainer);
    }

    if (ticketsContainer && mutationObserver) {
      mutationObserver.observe(ticketsContainer, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener('load', updateTicketsMinHeight);
    window.addEventListener('resize', updateTicketsMinHeight);
    const intervalId = window.setInterval(updateTicketsMinHeight, 500);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('load', updateTicketsMinHeight);
      window.removeEventListener('resize', updateTicketsMinHeight);
      window.clearInterval(intervalId);
    };
  }, [flightProvider, updateTicketsMinHeight]);

  useEffect(() => {
    const ticketsContainer = ticketsRef.current;

    setTicketResultsReady(false);
    setLowestDisplayedTicketPrice(null);

    if (!ticketsContainer || !payoutsSearchSubmitted) return undefined;

    let hasLoadedFlightMatrix = false;
    const observedRoots = new Set<HTMLElement | ShadowRoot>();
    const observers: MutationObserver[] = [];
    const hiddenMatrices = new Map<HTMLElement, { value: string; priority: string }>();

    const restoreMatrix = (matrix: HTMLElement) => {
      const previousDisplay = hiddenMatrices.get(matrix);

      if (!previousDisplay) return;

      if (previousDisplay.value) {
        matrix.style.setProperty('display', previousDisplay.value, previousDisplay.priority);
      } else {
        matrix.style.removeProperty('display');
      }

      hiddenMatrices.delete(matrix);
    };

    const updateReadyState = () => {
      const flightMatrices = getFlightMatrixElements(ticketsContainer);

      // Filters can mount later or inside a newly attached shadow root.
      hiddenMatrices.forEach((_, matrix) => {
        if (!flightMatrices.includes(matrix) || containsFlightFilters(matrix)) {
          restoreMatrix(matrix);
        }
      });

      if (flightMatrices.length) {
        hasLoadedFlightMatrix = true;
        flightMatrices.forEach((matrix) => {
          if (containsFlightFilters(matrix)) return;

          // Keep provider-owned nodes mounted so its rendering lifecycle stays intact.
          if (!hiddenMatrices.has(matrix)) {
            hiddenMatrices.set(matrix, {
              value: matrix.style.getPropertyValue('display'),
              priority: matrix.style.getPropertyPriority('display'),
            });
          }

          if (matrix.style.getPropertyValue('display') !== 'none'
            || matrix.style.getPropertyPriority('display') !== 'important') {
            matrix.style.setProperty('display', 'none', 'important');
          }
        });
      }

      setTicketResultsReady(hasLoadedFlightMatrix);
      setLowestDisplayedTicketPrice(getLowestDisplayedTicketPrice(getDeepSearchRoots(ticketsContainer), currency));
    };

    const observeAvailableRoots = () => {
      getDeepSearchRoots(ticketsContainer).forEach((root) => {
        if (observedRoots.has(root)) return;

        const observer = new MutationObserver(() => {
          observeAvailableRoots();
        });

        observer.observe(root, {
          attributes: true,
          attributeFilter: ['class', 'hidden', 'style'],
          childList: true,
          characterData: true,
          subtree: true,
        });
        observedRoots.add(root);
        observers.push(observer);
      });

      updateReadyState();
    };

    observeAvailableRoots();
    const shadowRootDiscoveryInterval = window.setInterval(observeAvailableRoots, 250);

    return () => {
      observers.forEach(observer => observer.disconnect());
      window.clearInterval(shadowRootDiscoveryInterval);
      hiddenMatrices.forEach((_, matrix) => restoreMatrix(matrix));
    };
  }, [currency, parsedFlightSearch, payoutsSearchSubmitted]);

  const handlePayoutsSearchClick = () => {
    setHideIntroText(true);
    setPayoutsSearchSubmitted(true);
    setShowMobilePayoutsWidget(false);
    setTicketResultsReady(false);
    setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      setParsedFlightSearch(parseFlightSearch(params.get('flightSearch')));
    }, 0);
    setShowPayoutsLoading(true);
    // show the loader for 5 seconds to avoid confusion while external widget loads
    setTimeout(() => setShowPayoutsLoading(false), 5000);
  };

  const handleMobilePayoutsSummaryClick = () => {
    setShowMobilePayoutsWidget(true);
  };

  const handleMobilePayoutsSummaryKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMobilePayoutsSummaryClick();
    }
  };

  const originLabel = parsedFlightSearch
    ? getLocationLabel(locationsByCode[parsedFlightSearch.originCode], parsedFlightSearch.originCode)
    : 'Origin';
  const destinationLabel = parsedFlightSearch?.destinationCode
    ? getLocationLabel(locationsByCode[parsedFlightSearch.destinationCode], parsedFlightSearch.destinationCode)
    : 'Destination';
  const departureLabel = formatTravelDate(parsedFlightSearch?.departureDate) || 'Departure';
  const returnLabel = formatTravelDate(parsedFlightSearch?.returnDate);
  const passengerCount = parsedFlightSearch?.passengerCount || 1;
  const travelClass = parsedFlightSearch?.travelClass || 'Economy';
  const originCityName = parsedFlightSearch
    ? getLocationCityName(locationsByCode[parsedFlightSearch.originCode], parsedFlightSearch.originCode)
    : '';
  const destinationCityName = parsedFlightSearch?.destinationCode
    ? getLocationCityName(locationsByCode[parsedFlightSearch.destinationCode], parsedFlightSearch.destinationCode)
    : '';
  const hasOriginOnlyRoute = Boolean(parsedFlightSearch?.originCode && !parsedFlightSearch.destinationCode);
  const canonicalUrl = 'https://www.tripenger.com/';
  const pageTitle = hasOriginOnlyRoute && originCityName
    ? `Flights from ${originCityName} | Cheap flights on Tripenger`
    : parsedFlightSearch && originCityName && destinationCityName
      ? `${originCityName} to ${destinationCityName} flights | Cheap flights on Tripenger`
      : 'Find Cheap Flights - Compare Cheaper Airfare Deals | Tripenger';
  const pageDescription = 'Find cheap flights with Tripenger. Compare cheaper airfare deals, discover better routes and fares, and book your next journey confidently with trusted flight providers.';
  const siteNavigationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: ['Search Flights', 'Request Itinerary'],
    url: [canonicalUrl, 'https://www.tripenger.com/itinerary'],
  };
  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Tripenger Flight Search',
    description: pageDescription,
    provider: {
      '@type': 'Organization',
      name: 'Tripenger',
      url: canonicalUrl,
    },
    serviceType: 'Cheap flight search and airfare comparison',
    url: canonicalUrl,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tripenger" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <script type="application/ld+json">
          {JSON.stringify(siteNavigationStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceStructuredData)}
        </script>
      </Helmet>
      {flightProvider === FlightSearchProvider.TRAVELPAYOUTS && <TripPayoutsProviderBuyTracker />}
      <Box
        sx={{
          display: shouldUseMobilePayoutsMode && !showMobilePayoutsWidget ? 'none' : 'block',
        }}
      >
        <FlightSearchHero
          flightSearchProvider={flightProvider}
          menuItems={shouldUseMobilePayoutsMode ? [] : [menuItem.searchFlight, menuItem.visaItinerary]}
          onPayoutsSearchClick={handlePayoutsSearchClick}
          widgetOnly={shouldUseMobilePayoutsMode}
          forceShowPayoutsSearch={showMobilePayoutsWidget}
          hideMobilePayoutsChangeButton
        />
      </Box>
      <Box
        pt={shouldUseMobilePayoutsMode ? 0 : 16}
        pb={25}
        bgcolor={'#fff'}
        sx={{
          '@media (max-width:1111px)': {
            pt: shouldUseMobilePayoutsMode ? 0 : 4,
          },
        }}
      >

        <Container>
          {shouldUseMobilePayoutsMode && (
            <Box
              sx={{
                '@keyframes mobilePayoutsSummaryIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-10px) scale(0.98)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0) scale(1)',
                  },
                },
                animation: !showMobilePayoutsWidget ? 'mobilePayoutsSummaryIn 280ms ease-out both' : 'none',
                display: { xs: 'block', md: 'none' },
                mb: 2.5,
                mx: { xs: -2, sm: 0 },
                px: { xs: 2, sm: 2.5 },
                py: 2,
                bgcolor: !showMobilePayoutsWidget ? 'rgba(5, 29, 64, .95)' : 'none',
                borderRadius: { xs: 0, sm: 3 },
                boxShadow: !showMobilePayoutsWidget ? '0 16px 36px rgba(5, 29, 64, 0.18)' : 'none',
              }}
            >
              {!showMobilePayoutsWidget && (
                <>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Change search details"
                    onClick={handleMobilePayoutsSummaryClick}
                    onKeyDown={handleMobilePayoutsSummaryKeyDown}
                    sx={{
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, #eef7ff 0%, #ddecff 100%)',
                      //border: '1px solid rgba(132, 190, 255, 0.88)',
                      borderRadius: 6,
                      boxShadow: '0 8px 22px rgba(0, 98, 227, 0.2), 0 3px 10px rgba(2, 18, 38, 0.18)',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 1.25,
                      minHeight: 50,
                      px: 1.75,
                      py: 0.65,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #f5fbff 0%, #d6e9ff 100%)',
                        borderColor: '#9fcaff',
                      },
                      '&:focus-visible': {
                        borderColor: '#69aefc',
                        outline: '2px solid rgba(255, 255, 255, 0.45)',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <SearchIcon sx={{ color: '#0062e3', flex: '0 0 auto' }} />
                    <Typography
                      sx={{
                        color: '#061b33',
                        flex: 1,
                        fontSize: 16,
                        fontWeight: 800,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {hasOriginOnlyRoute ? `Flights from ${originLabel}` : `${originLabel} → ${destinationLabel}`}
                    </Typography>
                  </Box>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Change date, passenger and class details"
                    onClick={handleMobilePayoutsSummaryClick}
                    onKeyDown={handleMobilePayoutsSummaryKeyDown}
                    sx={{
                      alignItems: 'center',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 1.25,
                      px: 0.5,
                      '&:focus-visible': {
                        borderRadius: 1,
                        outline: '2px solid rgba(255, 255, 255, 0.65)',
                        outlineOffset: 4,
                      },
                    }}
                  >
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75, minWidth: 0 }}>
                      {returnLabel ? <EventRepeatIcon fontSize="small" /> : <CalendarMonthIcon fontSize="small" />}
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {returnLabel ? `${departureLabel} - ${returnLabel}` : departureLabel}
                      </Typography>
                    </Box>
                    <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.75 }}>
                      <PeopleAltIcon fontSize="small" />
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {passengerCount} {passengerCount === 1 ? 'passenger' : 'passengers'} · {travelClass}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
          {flightProvider === FlightSearchProvider.TRAVELPAYOUTS && (
            <Box
              ref={ticketsWrapperRef}
              sx={{
                clear: 'both',
                display: 'flow-root',
                minHeight: ticketsMinHeight,
                position: 'relative',
                width: '100%',
              }}
            >
              <Fade
                in={Boolean(flexibleDatesOffer && ticketResultsReady
                  && flexibleDatesOffer.currency === currency
                  && lowestDisplayedTicketPrice !== null
                  && lowestDisplayedTicketPrice >= flexibleDatesOffer.price)}
                timeout={theme.transitions.duration.enteringScreen}
                unmountOnExit
              >
                <Paper
                  component="aside"
                  aria-label="Cheaper fares on alternative dates"
                  elevation={0}
                  sx={{
                    alignItems: { xs: 'stretch', md: 'center' },
                    bgcolor: '#fff',
                    border: '1px solid #dce7f5',
                    borderRadius: 4,
                    boxShadow: '0 4px 20px rgba(5, 29, 64, 0.05)',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 3 },
                    justifyContent: 'space-between',
                    mb: 2.5,
                    mt: { xs: -1, sm: -2 },
                    p: { xs: 2, sm: 3 },
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none !important',
                    },
                  }}
                >
                  <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                    <Box sx={{ alignItems: 'center', bgcolor: '#edf5ff', borderRadius: 3, color: '#0062e3', display: 'flex', flexShrink: 0, height: { xs: 44, sm: 52 }, justifyContent: 'center', width: { xs: 44, sm: 52 } }}>
                      <CalendarMonthIcon aria-hidden="true" sx={{ fontSize: 26 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h2" sx={{ color: '#051d40', fontSize: { xs: 18, sm: 20 }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                        Flexible with your dates?
                      </Typography>
                      <Typography sx={{ color: '#526580', fontSize: 14, lineHeight: 1.6, mt: 0.5 }}>
                        Explore cheaper return fares on alternative dates.
                      </Typography>
                      <Typography sx={{ color: '#051d40', fontSize: 14, fontWeight: 600, mt: 1, overflowWrap: 'anywhere' }}>
                        {flexibleDatesOffer?.originCityName} → {flexibleDatesOffer?.destinationCityName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ alignItems: 'center', borderTop: { xs: '1px solid #e8eef6', md: 'none' }, display: 'flex', flexDirection: 'row', flexShrink: 0, gap: { xs: 1.5, sm: 2.5 }, justifyContent: 'space-between', pt: { xs: 2, md: 0 } }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <Typography sx={{ color: '#526580', fontSize: 12, fontWeight: 600 }}>
                        Return fares from
                      </Typography>
                      <Typography sx={{ color: '#051d40', fontSize: { xs: 28, sm: 32 }, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.2, mt: 0.25 }}>
                        {flexibleDatesOffer?.formattedPrice}
                      </Typography>
                    </Box>
                    <Button
                      href={flexibleDatesOffer?.url}
                      onClick={(event) => {
                        if (isCheapestDatesLoading) {
                          event.preventDefault();
                          return;
                        }

                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                          setIsCheapestDatesLoading(true);
                        }
                      }}
                      disabled={isCheapestDatesLoading}
                      aria-busy={isCheapestDatesLoading}
                      startIcon={isCheapestDatesLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                      variant="contained"
                      disableElevation
                      sx={{
                        alignSelf: 'center',
                        bgcolor: '#0062e3',
                        borderRadius: 2.5,
                        flexShrink: { xs: 1, sm: 0 },
                        fontWeight: 800,
                        minHeight: 48,
                        px: { xs: 1.5, sm: 2.5 },
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#004fb8' },
                        '&:focus-visible': { outline: '3px solid #0062e3', outlineOffset: 3 },
                      }}
                    >
                      {isCheapestDatesLoading ? 'Loading cheapest dates…' : 'See cheapest dates'}
                    </Button>
                  </Box>
                </Paper>
              </Fade>
              <div id="tpwl-tickets" ref={ticketsRef}></div>
            </Box>
          )}
          {showPayoutsLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!hideIntroText && (
            <IcontTextXColumns
              iconContents={
                [
                  {
                    icon: SearchIcon,
                    content: {
                      title: 'Search Cheap Flights',
                      description: 'Enter your travel dates and destination to uncover affordable flight options from trusted travel providers.',
                    },
                  },
                  {
                    icon: TravelExploreIcon,
                    content: {
                      title: 'Compare Cheaper Fares',
                      description: 'Review routes, airlines, schedules, and prices side by side so you can spot stronger value before you book.',
                    },
                  },
                  {
                    icon: FlightTakeoffIcon,
                    content: {
                      title: 'Choose the Best-Value Flight',
                      description: 'Pick the fare that balances price, timing, and convenience for a smarter, more cost-effective journey.',
                    },
                  },
                  {
                    icon: PaymentIcon,
                    content: {
                      title: 'Book Securely with Confidence',
                      description: 'Complete your booking on the provider’s secure website after finding a flight deal that helps your budget go further.',
                    },
                  }
                ]
              }
            />
          )}
        </Container>
      </Box>

    </>
  );
}

export default FlightHomepage;
